// deployment_class: customer_env_allowed
/**
 * ErrorBoundary — global render-error boundary. Plan 14 §Task 10.
 *
 * Wraps the App so any render-time error in the route tree falls through
 * to a friendly explainer page with a "Return home" link. Errors are
 * logged to the registered error tracker (V&D Part 9 wires Sentry / a
 * similar integration; v1 just logs to the console).
 */
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

type ErrorTracker = (error: Error, info: ErrorInfo) => void;

let trackError: ErrorTracker = (error: Error, info: ErrorInfo) => {
  console.error('[ErrorBoundary] caught render error', error, info);
};

/**
 * Register a global render-error tracker. The boundary calls the registered
 * tracker (in addition to console logging) whenever it catches an error.
 * V&D Part 9 will install a real tracker (Sentry / Honeycomb / etc.).
 */
export function setErrorTracker(tracker: ErrorTracker): void {
  trackError = (error, info) => {
    try {
      tracker(error, info);
    } catch (trackerErr) {
      console.error('[ErrorBoundary] error tracker threw', trackerErr);
    }
  };
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    trackError(error, info);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-paper text-ink p-page">
          <div className="max-w-prose">
            <p className="font-body text-label uppercase text-warm-gray-d">Error</p>
            <h1 className="font-display text-display-1 mt-2">
              Something went wrong.
            </h1>
            <p className="mt-rhythm text-body-l text-warm-gray-d">
              An unexpected error occurred. The team has been notified.
            </p>
            <pre
              data-testid="error-boundary-message"
              className="mt-rhythm text-body-s text-warm-gray-d whitespace-pre-wrap"
            >
              {this.state.error.message}
            </pre>
            <a
              href="/"
              className="mt-rhythm inline-block text-trust underline underline-offset-2"
            >
              Return home
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
